import { DeliveryTypeEnum } from "../types/DeliveryType";
import { normalizeCheckoutPhone } from "./normalizeCheckoutPhone";

interface ContactCustomFieldDef {
  key: string;
  label: string;
  required?: boolean;
  options: string[];
}

export interface CheckoutResumeSessionInput {
  customerEmail?: string;
  customerPhone?: string;
  shipping?: {
    name: string;
    address: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      country?: string;
      postal_code?: string;
    };
  };
  contactCustomFields?: ContactCustomFieldDef[];
  contactCustomFieldValues?: Record<string, string>;
}

function shippingStoredComplete(
  shipping: CheckoutResumeSessionInput["shipping"] | undefined
): boolean {
  if (!shipping?.address) return false;
  const a = shipping.address;
  return Boolean(
    a.line1?.trim() &&
      a.city?.trim() &&
      a.postal_code?.trim() &&
      a.country?.trim()
  );
}

function cartSkipsShipping({
  products,
  lineItems,
}: {
  products: Array<{ _id: unknown; deliveryType?: string }>;
  lineItems: Array<{ productId?: unknown }>;
}): boolean {
  const byId = new Map(products.map((p) => [String(p._id), p]));
  const withPid = lineItems.filter((l) => l.productId != null);
  if (withPid.length === 0) return false;
  return withPid.every((l) => {
    const p = byId.get(String(l.productId));
    return p?.deliveryType === DeliveryTypeEnum.DIGITAL;
  });
}

function cartHasPayWhatYouCan({
  products,
  lineItems,
}: {
  products: Array<{ _id: unknown; payWhatYouCan?: boolean }>;
  lineItems: Array<{ productId?: unknown }>;
}): boolean {
  const byId = new Map(products.map((p) => [String(p._id), p]));
  for (const l of lineItems) {
    if (!l.productId) continue;
    const p = byId.get(String(l.productId));
    if (p?.payWhatYouCan === true) return true;
  }
  return false;
}

function contactInformationComplete({
  customerEmail,
  customerPhone,
  contactCustomFields,
  contactCustomFieldValues,
  hasPayWhatYouCan,
}: {
  customerEmail: string;
  customerPhone: string;
  contactCustomFields: ContactCustomFieldDef[] | undefined;
  contactCustomFieldValues: Record<string, string>;
  hasPayWhatYouCan: boolean;
}): boolean {
  if (hasPayWhatYouCan) return false;
  const emailOk = customerEmail.trim() !== "";
  const phoneNorm = normalizeCheckoutPhone(customerPhone);
  const phoneOk = phoneNorm.ok && Boolean(phoneNorm.e164);
  const defs = contactCustomFields;
  if (defs != null && defs.length > 0) {
    for (const d of defs) {
      const v = (contactCustomFieldValues[d.key] ?? "").trim();
      if (d.required) {
        if (!v || !d.options.includes(v)) return false;
      } else if (v !== "" && !d.options.includes(v)) return false;
    }
  }
  return emailOk && phoneOk;
}

/**
 * Deepest Checkout URL step that already has persisted data ("contact", "shipping", or "payment").
 */
export function initialCheckoutStepSlugFromSession({
  session,
  products,
  lineItems,
}: {
  session?: CheckoutResumeSessionInput;
  products: Array<{ _id: unknown; deliveryType?: string; payWhatYouCan?: boolean }>;
  lineItems: Array<{ productId?: unknown }>;
}): "contact" | "shipping" | "payment" {
  const skipsShipping = cartSkipsShipping({ products, lineItems });
  const hasPayWhatYouCan = cartHasPayWhatYouCan({ products, lineItems });
  const customerEmail = session?.customerEmail ?? "";
  const customerPhone = session?.customerPhone ?? "";
  const contactOk = contactInformationComplete({
    customerEmail,
    customerPhone,
    contactCustomFields: session?.contactCustomFields,
    contactCustomFieldValues: session?.contactCustomFieldValues ?? {},
    hasPayWhatYouCan,
  });
  if (!contactOk) return "contact";
  if (!skipsShipping && !shippingStoredComplete(session?.shipping)) return "shipping";
  return "payment";
}
